/* eslint-disable node/no-extraneous-import */
import React, { useEffect, useState } from "react";
import { Pagination } from "react-bootstrap";
import PaginationStyles from "../pagination/Pagination.styles";
import {
  DefaultPaginationProps,
  IPaginationProps,
  PAGINATION_PAGE_SIZE,
} from "./Pagination.types";
import { useSelector } from "react-redux";
import {
  setCurrentPage,
  setOffset,
} from "../../../state-management/slices/pagination/pagination.slice";
import { RootState } from "../../../state-management/root-reducer";
import { useHistory } from "react-router-dom";
import { PaginationController } from "./Pagination.controller";
import { AppDispatch, useAppDispatch } from "../../../state-management/store";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { UseHistoryType } from "../../../shared/types/routing.types";

const renderPageCollection = (
  pages: (string | number)[],
  currentPage: number,
  dispatch: AppDispatch,
  sendDataToGTM: any,
  paginationController: PaginationController,
  history: UseHistoryType
): React.ReactNode => {
  return (
    pages.length &&
    pages.map((item: number | string, index: number) => {
      /* istanbul ignore next */
      if (typeof item === "string") {
        return (
          <Pagination.Ellipsis
            data-testid={`pagination-ellipsis-${item}`}
            onClick={() => {
              dispatch(setCurrentPage(index + 1));
              dispatch(setOffset(index * PAGINATION_PAGE_SIZE));
              paginationController.triggerPageScroll(history);
              sendDataToGTM({
                event: "eventTracking",
                category: "Construction",
                action: "Click - Pagination",
                label: "Ellipsis",
              });
            }}
            key={`ellipis-${item}-INDEX_${Math.random() * index}`}
          />
        );
      } else {
        return (
          <Pagination.Item
            data-testid={`pagination-item-${item}`}
            onClick={() => {
              dispatch(setCurrentPage(item));
              dispatch(setOffset((item - 1) * PAGINATION_PAGE_SIZE));
              paginationController.triggerPageScroll(history);
              sendDataToGTM({
                event: "eventTracking",
                category: "Construction",
                action: "Click - Pagination",
                label: `page ${item}`,
              });
            }}
            value={item}
            active={currentPage === item}
            key={item}
          >
            {item}
          </Pagination.Item>
        );
      }
    })
  );
};

const PAGINATION: React.FC<IPaginationProps> = (props: IPaginationProps) => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const sendDataToGTM = useGTMDispatch();

  const paginationController = PaginationController.getInstance();

  // APP STATE
  const { currentPage } = useSelector((state: RootState) => state.pagination);

  // LOCAL STATE
  const { totalCount } = props;
  const [pages, setPages] = useState<Array<number | string>>([]);

  useEffect(() => {
    // istanbul ignore next
    if (totalCount) {
      const numPages = paginationController.getNumberOfPages(totalCount);

      // Use (number of pages) to build a proper
      // list of pagination items. (see controller for more details)
      if (numPages) {
        const pageCollection = paginationController.getPaginationCollection(
          currentPage,
          numPages
        );

        setPages(pageCollection);
      }
    }
  }, [currentPage, paginationController, totalCount]);

  return (
    <PaginationStyles.PaginationContainer
      data-testid="pagination-component"
      className="pagination-container"
      fluid="xl"
    >
      <Pagination>
        <Pagination.First
          disabled={currentPage === 1}
          onClick={
            /* istanbul ignore next */ () => {
              dispatch(setCurrentPage(1));
              dispatch(setOffset(0));
              paginationController.triggerPageScroll(history);
              sendDataToGTM({
                event: "eventTracking",
                category: "Construction",
                action: "Click - Pagination",
                label: "First Page",
              });
            }
          }
        />
        <Pagination.Prev
          disabled={currentPage === 1}
          onClick={
            /* istanbul ignore next */ () => {
              const newPageNumber = currentPage - 1;
              dispatch(setCurrentPage(newPageNumber));
              dispatch(setOffset((newPageNumber - 1) * PAGINATION_PAGE_SIZE));
              paginationController.triggerPageScroll(history);
              sendDataToGTM({
                event: "eventTracking",
                category: "Construction",
                action: "Click - Pagination",
                label: "Previous Page",
              });
            }
          }
        />

        {renderPageCollection(
          pages,
          currentPage,
          dispatch,
          sendDataToGTM,
          paginationController,
          history
        )}

        <Pagination.Next
          disabled={currentPage === (pages[pages.length - 1] as number)}
          onClick={
            /* istanbul ignore next */ () => {
              const newPageNumber = currentPage + 1;
              dispatch(setCurrentPage(newPageNumber));
              dispatch(setOffset((newPageNumber - 1) * PAGINATION_PAGE_SIZE));
              paginationController.triggerPageScroll(history);
              sendDataToGTM({
                event: "eventTracking",
                category: "Construction",
                action: "Click - Pagination",
                label: "Next Page",
              });
            }
          }
        />
        <Pagination.Last
          disabled={currentPage === (pages[pages.length - 1] as number)}
          onClick={
            /* istanbul ignore next */ () => {
              dispatch(setCurrentPage(pages[pages.length - 1] as number));
              dispatch(setOffset((pages.length - 1) * PAGINATION_PAGE_SIZE));
              paginationController.triggerPageScroll(history);
              sendDataToGTM({
                event: "eventTracking",
                category: "Construction",
                action: "Click - Pagination",
                label: "Last Page",
              });
            }
          }
        />
      </Pagination>
    </PaginationStyles.PaginationContainer>
  );
};

PAGINATION.defaultProps = DefaultPaginationProps;

export default PAGINATION;
